import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Hub, Logger } from '@aws-amplify/core';
import { AuthService } from "./service/auth-service";
import { config } from "./service/constants";
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { Alert, Box, InputAdornment } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { motion } from 'framer-motion';
import { ButtonWithEffects } from '../../components/ui/ButtonWithEffects';

const containerSx = { minHeight: '100vh', background: 'linear-gradient(135deg, #F8FAFC 0%, #E8EDF2 100%)' };
const authBoxSx = {
    width: '100%', maxWidth: 440, p: 6,
    background: 'rgba(255,255,255,0.9)', borderRadius: 4,
    boxShadow: '0 20px 60px rgba(0,0,0,0.08)', backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.5)',
};
const featureBoxSx = {
    p: 5, borderRadius: 4,
    background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.5)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
};

const features = [
    "Credit Card & Bank Statement Aggregation",
    "Transaction Tracking",
    "Receipt Tracking",
    "Detailed Spending Analysis",
    "Search Capabilities",
    "Subscription Insights",
    "Non-intrusive Data Collection",
];

export function RegisterForm() {
    const logger = new Logger("RegisterForm");
    const history = useHistory();
    const [errorMessage, setErrorMessage] = useState("");
    const [email, setEmail] = useState(localStorage.getItem('savedEmail') || '');
    const [password, setPassword] = useState('');

    useEffect(() => {
        const onHubCapsule = ({channel, payload}) => {
            if (channel === AuthService.CHANNEL && payload.event === AuthService.AUTH_EVENTS.REGISTER) {
                if (!payload.success) setErrorMessage(payload.message);
                else history.push(config.endpointAfterRegistration);
            }
        };
        Hub.listen(AuthService.CHANNEL, onHubCapsule, 'MyListener');
        return () => Hub.remove(AuthService.CHANNEL, onHubCapsule);
    }, []);

    const onFinish = (e) => {
        e.preventDefault();
        localStorage.setItem('savedEmail', email);
        AuthService.register(email, password);
    };

    return (
        <Grid container component="main" sx={containerSx}>
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                <motion.div initial={{opacity: 0, x: -20}} animate={{opacity: 1, x: 0}} transition={{duration: 0.5}}
                    style={{ width: '100%', maxWidth: 480 }}>
                    <Box sx={featureBoxSx}>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: 'primary.main' }}>
                            What's Included
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {features.map((f, i) => (
                                <motion.div key={f} initial={{opacity: 0, x: -10}} animate={{opacity: 1, x: 0}}
                                    transition={{delay: 0.1 * i, duration: 0.3}}>
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                        p: 1.5, borderRadius: 2,
                                        transition: 'all 0.2s ease',
                                        '&:hover': { background: 'rgba(99,102,241,0.04)', transform: 'translateX(4px)' },
                                    }}>
                                        <CheckCircleOutlineIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }}/>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{f}</Typography>
                                    </Box>
                                </motion.div>
                            ))}
                        </Box>
                    </Box>
                </motion.div>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{duration: 0.5, delay: 0.15}}>
                    <Box sx={authBoxSx}>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <img src={window.location.origin + '/logos/logo-md.png'} style={{width: 160}} alt=""/>
                        </Box>
                        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700 }}>
                            Create Account
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mb: 3 }}>
                            Start managing your finances today
                        </Typography>
                        {errorMessage && (
                            <motion.div initial={{opacity: 0, y: -10}} animate={{opacity: 1, y: 0}}>
                                <Alert severity="error" sx={{mb: 2}}>{errorMessage}</Alert>
                            </motion.div>
                        )}
                        <form noValidate onSubmit={onFinish}>
                            <TextField variant="outlined" margin="normal" required fullWidth
                                label="Email Address" autoComplete="email" autoFocus
                                value={email} onInput={e => setEmail(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon color="primary"/></InputAdornment> }}
                            />
                            <TextField variant="outlined" margin="normal" required fullWidth
                                label="Password" type="password" autoComplete="new-password"
                                value={password} onInput={e => setPassword(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="primary"/></InputAdornment> }}
                            />
                            <ButtonWithEffects type="submit" fullWidth sx={{ mt: 3, mb: 2 }}>
                                Create Account
                            </ButtonWithEffects>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Link to="/forgotpassword1" style={{ fontSize: '0.875rem' }}>Forgot password?</Link>
                                <Link to="/login" style={{ fontSize: '0.875rem' }}>Already registered? Sign in</Link>
                            </Box>
                        </form>
                    </Box>
                </motion.div>
            </Grid>
        </Grid>
    );
}
